import styled from "styled-components";
import { ArrowLeft } from "lucide-react";
import {
  ComponentProps,
  createContext,
  forwardRef,
  useContext,
  useLayoutEffect,
} from "react";
import { createPortal } from "react-dom";
import { OnboardingContext } from "@shared/features/onboarding/Onboarding.context";
import { QuickConnectContext } from "@shared/features/onboarding/QuickConnect.context";
import { OnboardingDetailedStep } from "@shared/features/onboarding/types";
import { ONBOARDING_BACK_ID } from "testIds";

interface BackButtonProps {
  detailedStep?: OnboardingDetailedStep;
}

const BackButton = ({ detailedStep }: BackButtonProps) => {
  const { backButtonTitle, onBackButtonClick } = useContext(OnboardingContext)!;
  const { onBackToSelectExchange } = useContext(QuickConnectContext)!;

  const onBackClick = () => {
    onBackButtonClick();
    if (detailedStep === OnboardingDetailedStep.CONNECT_EXCHANGE) {
      onBackToSelectExchange();
    }
  };

  return (
    <BackLinkButton
      type="button"
      aria-label={backButtonTitle}
      data-testid={ONBOARDING_BACK_ID}
      onClick={onBackClick}
    >
      <ArrowLeft size={16} strokeWidth={1.75} aria-hidden />
      {backButtonTitle}
    </BackLinkButton>
  );
};

export default BackButton;

const BackLinkButton = styled.button`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  gap: 6px;
  margin: 0;
  padding: 8px 0;
  min-height: 40px;
  border: 0;
  background: transparent;
  color: ${(p) => p.theme.colors.primaryBlue};
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.2;
  cursor: pointer;
  @media (max-width: 650px) {
    margin-bottom: 8px;
  }
  svg {
    flex: none;
  }
  &:hover {
    opacity: 0.8;
  }
  &:focus-visible {
    outline: 2px solid ${(p) => p.theme.colors.primaryBlue};
    outline-offset: 4px;
    border-radius: 4px;
  }
`;

// Every back control (the provider's BackButton or a step's own
// OnboardingBackLink) renders into one slot at the top of the step column, so
// it never changes position between steps.
interface BackSlotValue {
  slot: HTMLElement | null;
  // A step that renders its own link registers it so the provider's
  // BackButton steps aside instead of showing a second one.
  registerCustomLink: () => () => void;
}
export const OnboardingBackSlotContext = createContext<BackSlotValue | null>(
  null
);

export const OnboardingBackLink = forwardRef<
  HTMLButtonElement,
  ComponentProps<typeof BackLinkButton>
>((props, ref) => {
  const ctx = useContext(OnboardingBackSlotContext);
  const registerCustomLink = ctx?.registerCustomLink;
  useLayoutEffect(() => registerCustomLink?.(), [registerCustomLink]);
  const link = <BackLinkButton ref={ref} {...props} />;
  if (!ctx) return link;
  return ctx.slot ? createPortal(link, ctx.slot) : null;
});
OnboardingBackLink.displayName = "OnboardingBackLink";
